/**
 * Per-chapter durable outbox for the desktop app.
 *
 * Chapter prose has exactly one writer, and this is it. The whole-book push
 * (`/api/desktop/books/sync`) carries structure only; every body change — typing,
 * AI variations, translate, applied edits — is enqueued here, persisted to
 * IndexedDB, and retried across restarts until the server acknowledges it.
 */

import { ChapterSyncController } from "../../shared/chapterSync/sync/chapterSyncController";
import { getChapterSyncStorage } from "../../shared/chapterSync/storage/idbChapterStore";
import { chapterBodyFingerprint } from "../../shared/chapterSync/fingerprint";
import type {
  ChapterJournalEntry,
  ChapterSyncPushFn,
  ChapterSyncPushResult,
  ChapterSyncStatus,
} from "../../shared/chapterSync/types";
import { serializeChapterContent } from "../../shared/chapterContent";
import type { Book, Chapter } from "../../shared/models";
import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";

/** Storage scope when nobody is signed in yet. Work is still journalled locally. */
const ANONYMOUS_USER_ID = "local-device";

export type ChapterSyncedInfo = {
  chapterId: string;
  rev: number;
  title: string;
  contentJson: string;
  updatedAt: string;
};

function seedOf(chapter: Chapter): { title: string; contentJson: string; rev: number } {
  const base = chapter.metadata?.syncBase;
  if (base?.content) {
    return {
      title: base.title ?? chapter.title,
      contentJson: serializeChapterContent(base.content),
      rev: typeof base.rev === "number" && Number.isFinite(base.rev) ? base.rev : 0,
    };
  }
  const rev =
    typeof chapter.rev === "number" && Number.isFinite(chapter.rev)
      ? chapter.rev
      : typeof chapter.baseRev === "number" && Number.isFinite(chapter.baseRev)
        ? chapter.baseRev
        : 0;
  return {
    title: chapter.title,
    contentJson: serializeChapterContent(chapter.content),
    rev,
  };
}

function liveOf(chapter: Chapter): { title: string; contentJson: string } {
  return { title: chapter.title, contentJson: serializeChapterContent(chapter.content) };
}

class BookChapterSyncManager {
  private controllers = new Map<string, ChapterSyncController>();
  /** Fingerprint of the last body we handed to each chapter's controller. */
  private enqueued = new Map<string, string>();
  /**
   * The raw (already-normalized) body and title last inspected per chapter.
   * `Chapter.content` is a string by the time it reaches us, so an untouched
   * chapter costs one string compare instead of a parse/serialize/fingerprint
   * pass. Without this, every keystroke re-serialized the whole manuscript.
   */
  private lastSeen = new Map<string, { content: unknown; title: string }>();
  private scanTimer: number | null = null;
  private pendingScanBook: Book | null = null;
  private bookId = "";
  private userId = ANONYMOUS_USER_ID;
  private onSynced: ((info: ChapterSyncedInfo) => void) | null = null;
  private onStatus: ((chapterId: string, status: ChapterSyncStatus, detail?: string) => void) | null =
    null;

  setListeners(listeners: {
    onSynced?: (info: ChapterSyncedInfo) => void;
    onStatus?: (chapterId: string, status: ChapterSyncStatus, detail?: string) => void;
  }): void {
    this.onSynced = listeners.onSynced ?? null;
    this.onStatus = listeners.onStatus ?? null;
  }

  /**
   * Point the manager at a book. Switching books (or accounts) tears down the
   * controllers, but never the IndexedDB outbox — pending work outlives them.
   */
  attach(book: Book | null): void {
    const nextBookId = book?.id?.trim() ?? "";
    const nextUserId = cloudAuthDesktopV2.getUserId() ?? ANONYMOUS_USER_ID;
    if (nextBookId === this.bookId && nextUserId === this.userId) return;
    this.teardownControllers();
    this.bookId = nextBookId;
    this.userId = nextUserId;
  }

  private buildPush(): ChapterSyncPushFn {
    return async (input): Promise<ChapterSyncPushResult> => {
      if (!cloudAuthDesktopV2.isSignedIn()) {
        // Undelivered rather than failed: keep the outbox item and the work.
        return { ok: false, status: 0, error: "Not signed in", offline: true };
      }
      let content: unknown;
      try {
        content = JSON.parse(input.contentJson);
      } catch {
        return { ok: false, status: 0, error: "Chapter body is not valid JSON" };
      }

      const result = await cloudAuthDesktopV2.patchChapter({
        bookId: input.bookId,
        chapterId: input.chapterId,
        title: input.title,
        content,
        wordCount: input.wordCount,
        baseRev: input.baseRev,
        force: input.force,
      });

      if (result.ok) {
        return {
          ok: true,
          rev: result.rev,
          updatedAt: result.updatedAt,
          title: input.title,
          contentJson: input.contentJson,
        };
      }
      if ("remote" in result) {
        const remote =
          result.remote ??
          (await cloudAuthDesktopV2.getChapter(input.bookId, input.chapterId).catch(() => null));
        if (!remote) {
          return { ok: false, status: 409, error: "Chapter conflict with no server copy" };
        }
        return {
          ok: false,
          status: 409,
          remote: {
            title: remote.title,
            content: remote.content,
            rev: remote.rev,
            updatedAt: remote.updatedAt,
          },
        };
      }
      return { ok: false, status: result.status, error: result.error, offline: result.offline };
    };
  }

  private controllerFor(chapter: Chapter): ChapterSyncController | null {
    if (!this.bookId || !chapter.id) return null;
    const existing = this.controllers.get(chapter.id);
    if (existing) return existing;

    const controller = new ChapterSyncController({
      userId: this.userId,
      bookId: this.bookId,
      chapterId: chapter.id,
      push: this.buildPush(),
      fetchRemote: async ({ bookId, chapterId }) =>
        cloudAuthDesktopV2.getChapter(bookId, chapterId).catch(() => null),
      // A single desktop window owns its book; the leader election only adds a
      // way for a save to silently go nowhere.
      multiTabLeader: false,
      onStatus: (status, detail) => this.onStatus?.(chapter.id, status, detail),
      onSynced: (info) => this.onSynced?.({ chapterId: chapter.id, ...info }),
    });
    this.controllers.set(chapter.id, controller);

    const seed = seedOf(chapter);
    this.enqueued.set(chapter.id, chapterBodyFingerprint(seed.title, seed.contentJson));
    // Also drains anything this chapter left in the outbox from a prior session.
    void controller.hydrateFromServer(seed);
    return controller;
  }

  /**
   * Queue a scan of the book. Called on every `bookRef` mutation, which includes
   * every draft flush while typing, so the work is coalesced onto a later task
   * rather than run inside the editor's update handler.
   */
  enqueueChangedChapters(book: Book | null): void {
    if (!book) return;
    this.pendingScanBook = book;
    if (this.scanTimer != null) return;
    this.scanTimer = window.setTimeout(() => {
      this.scanTimer = null;
      const pending = this.pendingScanBook;
      this.pendingScanBook = null;
      if (pending) this.scanNow(pending);
    }, 0);
  }

  /**
   * Diff every chapter body against what we last handed the outbox and enqueue
   * the ones that moved.
   */
  private scanNow(book: Book): void {
    this.attach(book);
    if (!this.bookId) return;

    const seen = new Set<string>();
    for (const chapter of book.chapters) {
      if (!chapter.id) continue;
      seen.add(chapter.id);

      // Cheap rejection for the chapters this edit did not touch. The first
      // scan of a book always falls through, so every chapter still gets a
      // controller and drains whatever a prior session left in the outbox.
      const previous = this.lastSeen.get(chapter.id);
      if (previous && previous.content === chapter.content && previous.title === chapter.title) {
        continue;
      }

      const controller = this.controllerFor(chapter);
      if (!controller) continue;
      this.lastSeen.set(chapter.id, { content: chapter.content, title: chapter.title });

      const live = liveOf(chapter);
      const fingerprint = chapterBodyFingerprint(live.title, live.contentJson);
      if (this.enqueued.get(chapter.id) === fingerprint) continue;
      this.enqueued.set(chapter.id, fingerprint);
      controller.noteLocalEdit(live.title, live.contentJson);
    }

    for (const [chapterId, controller] of this.controllers) {
      if (seen.has(chapterId)) continue;
      controller.destroy();
      this.controllers.delete(chapterId);
      this.enqueued.delete(chapterId);
      this.lastSeen.delete(chapterId);
    }
  }

  /** Persist and push one chapter now, skipping the debounce. */
  async flushChapter(book: Book | null, chapterId: string): Promise<void> {
    if (!book || !chapterId) return;
    const chapter = book.chapters.find((c) => c.id === chapterId);
    if (!chapter) return;
    this.attach(book);
    const controller = this.controllerFor(chapter);
    if (!controller) return;
    const live = liveOf(chapter);
    this.enqueued.set(chapterId, chapterBodyFingerprint(live.title, live.contentJson));
    this.lastSeen.set(chapterId, { content: chapter.content, title: chapter.title });
    await controller.flushLocal(live.title, live.contentJson);
  }

  /** Persist every changed chapter now. Used on unload and explicit Save. */
  async flushAll(book: Book | null): Promise<void> {
    if (!book) return;
    this.cancelScheduledScan();
    this.scanNow(book);
    await Promise.all(
      book.chapters.map(async (chapter) => {
        const controller = this.controllers.get(chapter.id);
        if (!controller) return;
        const live = liveOf(chapter);
        await controller.flushLocal(live.title, live.contentJson).catch(() => undefined);
      })
    );
  }

  /** Retry anything the outbox is still holding (sign-in, regained network). */
  async drainAll(): Promise<void> {
    await Promise.all(
      [...this.controllers.values()].map((c) => c.drainOutbox().catch(() => undefined))
    );
  }

  /**
   * Re-seed a chapter after an authoritative write that bypassed the outbox
   * (explicit Save, history restore), so a stale queued push cannot undo it.
   */
  async acknowledgeAuthoritative(
    chapterId: string,
    input: { title: string; contentJson: string; rev: number }
  ): Promise<void> {
    const controller = this.controllers.get(chapterId);
    if (!controller) return;
    this.enqueued.set(chapterId, chapterBodyFingerprint(input.title, input.contentJson));
    this.lastSeen.delete(chapterId);
    await controller.acknowledgeAuthoritativeSave(input);
  }

  /** Local record of every save and failed attempt for one chapter. */
  async listJournal(chapterId: string): Promise<ChapterJournalEntry[]> {
    const controller = this.controllers.get(chapterId);
    if (controller) return controller.listJournal();
    if (!this.bookId) return [];
    // No controller yet (panel opened before the chapter was touched): read the
    // durable store directly so prior sessions still show up.
    return getChapterSyncStorage()
      .listJournalForChapter(this.userId, this.bookId, chapterId)
      .catch(() => []);
  }

  /** True when at least one chapter still has work the server has not accepted. */
  hasPendingWork(): boolean {
    for (const controller of this.controllers.values()) {
      const status = controller.getStatus();
      if (status === "dirty" || status === "localSaved" || status === "pushing") return true;
      if (status === "offline" || status === "error" || status === "conflict") return true;
    }
    return false;
  }

  private cancelScheduledScan(): void {
    if (this.scanTimer != null) window.clearTimeout(this.scanTimer);
    this.scanTimer = null;
    this.pendingScanBook = null;
  }

  private teardownControllers(): void {
    for (const controller of this.controllers.values()) controller.destroy();
    this.controllers.clear();
    this.enqueued.clear();
    this.lastSeen.clear();
  }

  destroy(): void {
    this.cancelScheduledScan();
    this.teardownControllers();
    this.bookId = "";
  }
}

export const bookChapterSync = new BookChapterSyncManager();
